import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useParams } from "wouter";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { usePageTitle } from "@/hooks/use-page-title";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, List, ListOrdered, Heading2, Heading3,
  Quote, Code, Minus, ArrowLeft, Save, Eye, EyeOff, Upload, X,
} from "lucide-react";
import { useUpload } from "@workspace/object-storage-web";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  coverImage: string | null;
  status: string;
  publishedAt: string | null;
}

function slugify(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 200);
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "2rem",
        height: "2rem",
        borderRadius: "0.375rem",
        border: "none",
        background: active ? "hsl(var(--primary))" : "transparent",
        color: active ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        transition: "background 0.1s, color 0.1s",
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

export default function AdminBlogEditor() {
  const { id } = useParams<{ id?: string }>();
  const isNew = !id || id === "new";
  const [, navigate] = useLocation();

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [excerpt, setExcerpt] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [coverImageError, setCoverImageError] = useState(false);
  const [status, setStatus] = useState<"draft" | "published">("draft");
  const [publishedAt, setPublishedAt] = useState("");
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadFile, isUploading } = useUpload({
    basePath: `${BASE}/api/storage/public-uploads`,
    onSuccess: (response) => {
      const url = `${BASE}${response.objectPath}`;
      setCoverImage(url);
      setCoverImageError(false);
    },
    onError: (err) => {
      setError(`Image upload failed: ${err.message}`);
    },
  });

  usePageTitle(isNew ? "Admin – New Post" : "Admin – Edit Post");

  const editor = useEditor({
    extensions: [StarterKit],
    content: "",
    editorProps: {
      attributes: {
        style: "min-height: 320px; outline: none; padding: 1rem; font-size: 0.9375rem; line-height: 1.7;",
      },
    },
  });

  useEffect(() => {
    if (isNew || !id) return;
    fetch(`${BASE}/api/admin/blog/${id}`, { credentials: "include" })
      .then(r => {
        if (r.status === 404) return Promise.reject("not_found");
        if (!r.ok) return Promise.reject("error");
        return r.json();
      })
      .then((post: BlogPost) => {
        setTitle(post.title);
        setSlug(post.slug);
        setSlugManual(true);
        setExcerpt(post.excerpt ?? "");
        setCoverImage(post.coverImage ?? "");
        setCoverImageError(false);
        setStatus(post.status as "draft" | "published");
        setPublishedAt(post.publishedAt ? post.publishedAt.slice(0, 10) : "");
        if (editor) editor.commands.setContent(post.content || "");
        setLoading(false);
      })
      .catch((reason) => {
        setError(reason === "not_found" ? "Post not found" : "Failed to load post");
        setLoading(false);
      });
  }, [id, isNew, editor]);

  const handleTitleChange = useCallback((val: string) => {
    setTitle(val);
    if (!slugManual) {
      setSlug(slugify(val));
    }
  }, [slugManual]);

  const handleSlugChange = useCallback((val: string) => {
    setSlug(val.toLowerCase().replace(/[^a-z0-9-]/g, "").replace(/-+/g, "-"));
    setSlugManual(true);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) { setError("Title is required"); return; }
    if (!slug.trim()) { setError("Slug is required"); return; }

    setSaving(true);
    setError("");

    const content = editor?.getHTML() ?? "";
    const body = {
      title: title.trim(),
      slug: slug.trim(),
      content,
      excerpt: excerpt.trim() || null,
      coverImage: coverImage.trim() || null,
      status,
      publishedAt: publishedAt ? new Date(publishedAt).toISOString() : null,
    };

    try {
      const url = isNew ? `${BASE}/api/admin/blog` : `${BASE}/api/admin/blog/${id}`;
      const method = isNew ? "POST" : "PUT";
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to save");
      }
      navigate("/admin/blog");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "3rem", textAlign: "center", color: "hsl(var(--muted-foreground))" }}>
        Loading post…
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "900px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", marginBottom: "1.5rem" }}>
        <button
          type="button"
          onClick={() => navigate("/admin/blog")}
          style={{ display: "flex", alignItems: "center", gap: "0.375rem", background: "none", border: "none", cursor: "pointer", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem", padding: "0.25rem 0.5rem", borderRadius: "0.375rem" }}
        >
          <ArrowLeft style={{ width: "1rem", height: "1rem" }} />
          Back to Blog
        </button>
        <h1 style={{ fontSize: "1.5rem", fontWeight: 700 }}>
          {isNew ? "New Post" : "Edit Post"}
        </h1>
      </div>

      {error && (
        <div style={{ padding: "0.875rem 1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))", marginBottom: "1.25rem", fontSize: "0.9rem" }}>
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div style={{ display: "grid", gap: "1.25rem" }}>
          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
              Title <span style={{ color: "#ef4444" }}>*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={e => handleTitleChange(e.target.value)}
              placeholder="Post title"
              required
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
                fontSize: "1.0625rem",
                fontWeight: 600,
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
              Slug <span style={{ color: "#ef4444" }}>*</span>
              <span style={{ fontWeight: 400, color: "hsl(var(--muted-foreground))", marginLeft: "0.5rem" }}>
                (auto-generated from title)
              </span>
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem", whiteSpace: "nowrap" }}>/blog/</span>
              <input
                type="text"
                value={slug}
                onChange={e => handleSlugChange(e.target.value)}
                placeholder="post-slug"
                required
                pattern="^[a-z0-9-]+$"
                title="Lowercase letters, numbers, and hyphens only"
                style={{
                  flex: 1,
                  padding: "0.5rem 0.75rem",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  background: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                  outline: "none",
                  fontFamily: "monospace",
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
              Excerpt
              <span style={{ fontWeight: 400, color: "hsl(var(--muted-foreground))", marginLeft: "0.5rem" }}>
                (shown on the blog list page)
              </span>
            </label>
            <textarea
              value={excerpt}
              onChange={e => setExcerpt(e.target.value)}
              placeholder="A short summary of the post…"
              rows={3}
              style={{
                width: "100%",
                padding: "0.625rem 0.875rem",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
                fontSize: "0.9375rem",
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))",
                outline: "none",
                resize: "vertical",
                boxSizing: "border-box",
                fontFamily: "inherit",
              }}
            />
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
              Cover Image
              <span style={{ fontWeight: 400, color: "hsl(var(--muted-foreground))", marginLeft: "0.5rem" }}>
                (optional — used as hero image and for Google rich results)
              </span>
            </label>
            <div style={{ display: "flex", gap: "0.5rem", alignItems: "stretch" }}>
              <input
                type="text"
                value={coverImage}
                onChange={e => { setCoverImage(e.target.value); setCoverImageError(false); }}
                placeholder="https://example.com/image.jpg or upload below"
                style={{
                  flex: 1,
                  padding: "0.625rem 0.875rem",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.5rem",
                  fontSize: "0.9375rem",
                  background: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                  outline: "none",
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                title="Upload image from your computer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.375rem",
                  padding: "0.625rem 0.875rem",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.5rem",
                  background: "hsl(var(--muted) / 0.5)",
                  color: "hsl(var(--foreground))",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  cursor: isUploading ? "not-allowed" : "pointer",
                  opacity: isUploading ? 0.6 : 1,
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                <Upload style={{ width: "0.875rem", height: "0.875rem" }} />
                {isUploading ? "Uploading…" : "Upload"}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const MAX_SIZE = 10 * 1024 * 1024;
                  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/svg+xml", "image/avif"];
                  if (!ALLOWED_TYPES.includes(file.type)) {
                    setError("Only image files are allowed (JPEG, PNG, WebP, GIF, SVG, AVIF).");
                    e.target.value = "";
                    return;
                  }
                  if (file.size > MAX_SIZE) {
                    setError("Image must be 10 MB or smaller.");
                    e.target.value = "";
                    return;
                  }
                  await uploadFile(file);
                  e.target.value = "";
                }}
              />
            </div>
            {coverImage && !coverImageError && (
              <div style={{ position: "relative", marginTop: "0.625rem", display: "inline-block", width: "100%" }}>
                <img
                  src={coverImage}
                  alt="Cover preview"
                  style={{
                    width: "100%",
                    maxHeight: "180px",
                    objectFit: "cover",
                    borderRadius: "0.5rem",
                    border: "1px solid hsl(var(--border))",
                    display: "block",
                  }}
                  onError={() => setCoverImageError(true)}
                />
                <button
                  type="button"
                  onClick={() => { setCoverImage(""); setCoverImageError(false); }}
                  title="Remove cover image"
                  style={{
                    position: "absolute",
                    top: "0.375rem",
                    right: "0.375rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "1.5rem",
                    height: "1.5rem",
                    borderRadius: "50%",
                    border: "none",
                    background: "rgba(0,0,0,0.55)",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <X style={{ width: "0.75rem", height: "0.75rem" }} />
                </button>
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
                Status
              </label>
              <div style={{ display: "flex", gap: "0.75rem" }}>
                {(["draft", "published"] as const).map(s => (
                  <label
                    key={s}
                    style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", fontSize: "0.9375rem" }}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={s}
                      checked={status === s}
                      onChange={() => setStatus(s)}
                    />
                    <span style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                      {s === "published" ? <Eye style={{ width: "0.875rem", height: "0.875rem", color: "#10b981" }} /> : <EyeOff style={{ width: "0.875rem", height: "0.875rem", color: "#6b7280" }} />}
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
                Publish Date
                <span style={{ fontWeight: 400, color: "hsl(var(--muted-foreground))", marginLeft: "0.5rem" }}>
                  (optional)
                </span>
              </label>
              <input
                type="date"
                value={publishedAt}
                onChange={e => setPublishedAt(e.target.value)}
                style={{
                  padding: "0.5rem 0.75rem",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "0.5rem",
                  fontSize: "0.875rem",
                  background: "hsl(var(--background))",
                  color: "hsl(var(--foreground))",
                  outline: "none",
                }}
              />
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.875rem", marginBottom: "0.375rem" }}>
              Content
            </label>
            <div style={{
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.5rem",
              overflow: "hidden",
              background: "hsl(var(--background))",
            }}>
              {editor && (
                <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "0.125rem",
                  padding: "0.5rem 0.75rem",
                  borderBottom: "1px solid hsl(var(--border))",
                  background: "hsl(var(--muted) / 0.4)",
                }}>
                  <ToolbarButton
                    title="Bold"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    active={editor.isActive("bold")}
                  >
                    <Bold style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Italic"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    active={editor.isActive("italic")}
                  >
                    <Italic style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <div style={{ width: "1px", background: "hsl(var(--border))", margin: "0.25rem 0.375rem" }} />
                  <ToolbarButton
                    title="Heading 2"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    active={editor.isActive("heading", { level: 2 })}
                  >
                    <Heading2 style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Heading 3"
                    onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                    active={editor.isActive("heading", { level: 3 })}
                  >
                    <Heading3 style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <div style={{ width: "1px", background: "hsl(var(--border))", margin: "0.25rem 0.375rem" }} />
                  <ToolbarButton
                    title="Bullet list"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    active={editor.isActive("bulletList")}
                  >
                    <List style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Ordered list"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    active={editor.isActive("orderedList")}
                  >
                    <ListOrdered style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <div style={{ width: "1px", background: "hsl(var(--border))", margin: "0.25rem 0.375rem" }} />
                  <ToolbarButton
                    title="Blockquote"
                    onClick={() => editor.chain().focus().toggleBlockquote().run()}
                    active={editor.isActive("blockquote")}
                  >
                    <Quote style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Code block"
                    onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                    active={editor.isActive("codeBlock")}
                  >
                    <Code style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                  <ToolbarButton
                    title="Horizontal rule"
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                  >
                    <Minus style={{ width: "0.875rem", height: "0.875rem" }} />
                  </ToolbarButton>
                </div>
              )}
              <EditorContent editor={editor} />
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", paddingTop: "0.5rem" }}>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/admin/blog")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Save style={{ width: "1rem", height: "1rem" }} />
              {saving ? "Saving…" : isNew ? "Create Post" : "Save Changes"}
            </Button>
          </div>
        </div>
      </form>

      <style>{`
        .tiptap { min-height: 320px; outline: none; }
        .tiptap p { margin-bottom: 0.875rem; }
        .tiptap h2 { font-size: 1.375rem; font-weight: 700; margin: 1.25rem 0 0.5rem; }
        .tiptap h3 { font-size: 1.125rem; font-weight: 700; margin: 1rem 0 0.5rem; }
        .tiptap ul { list-style: disc; padding-left: 1.5rem; margin-bottom: 0.875rem; }
        .tiptap ol { list-style: decimal; padding-left: 1.5rem; margin-bottom: 0.875rem; }
        .tiptap li { margin-bottom: 0.25rem; }
        .tiptap blockquote { border-left: 4px solid hsl(var(--border)); padding-left: 1rem; color: hsl(var(--muted-foreground)); margin: 1rem 0; }
        .tiptap pre { background: hsl(var(--muted)); padding: 0.875rem; border-radius: 0.5rem; font-family: monospace; font-size: 0.875rem; margin-bottom: 0.875rem; overflow-x: auto; }
        .tiptap code { background: hsl(var(--muted)); padding: 0.125rem 0.375rem; border-radius: 0.25rem; font-size: 0.875em; font-family: monospace; }
        .tiptap pre code { background: none; padding: 0; }
        .tiptap hr { border: none; border-top: 1px solid hsl(var(--border)); margin: 1.5rem 0; }
        .tiptap strong { font-weight: 700; }
        .tiptap em { font-style: italic; }
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          height: 0;
        }
      `}</style>
    </div>
  );
}
